import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';
import ConditionalTooltip from '../conditionalTooltip';
import { ellipsis } from '../../../constants/helperCssRules';
import { getColumnWidth, getAlignItem } from '../../../constants/gridTable';
import { isEllipsisActive } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    display: 'grid',
  },
  row: {
    display: 'grid',
    alignContent: 'center',
    backgroundColor: primary.bluish9,
    marginBottom: spacing(2),
    textDecoration: 'none',
    '&:last-of-type': {
      marginBottom: spacing(0),
    },
  },
  rowCell: {
    alignSelf: 'center',
    boxSizing: 'border-box',
    color: primary.bluish1,
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    ...ellipsis(),
  },
  cellValue: {
    ...ellipsis(),
  },
  highlightedText: {
    fontFamily: 'ProximaNova-Bold',
  },
  link: {
    textDecoration: 'none',
  },
}));

const GridTablePlainBody = ({ headers, rows, prepareDetailsUrl }) => {
  const [cellsWithTooltip, setCellsWithTooltip] = useState([]);
  const cellsTooltipRef = useRef([]);
  const classes = useStyles();

  useEffect(() => {
    setCellsWithTooltip(
      cellsTooltipRef.current.map(cell => {
        const cellWithTooltip = {};

        Object.keys(cell).forEach(cellKey => {
          cellWithTooltip[cellKey] = isEllipsisActive(
            cell[cellKey],
            false,
            true
          );
        });

        return cellWithTooltip;
      })
    );
  }, [rows]);

  const handleActionCellClick = isHeaderAction => e => {
    if (isHeaderAction) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const getCellRef = (element, rowIndex, rowKey, hasTooltip) => {
    if (hasTooltip) {
      cellsTooltipRef.current[rowIndex] = {
        ...(cellsTooltipRef.current[rowIndex]
          ? {
              ...cellsTooltipRef.current[rowIndex],
              [rowKey]: element,
            }
          : { [rowKey]: element }),
      };
    }
  };

  const renderCellValue = (cellValue, header, rowIndex, isHighlighted) => {
    return (
      <ConditionalTooltip
        addTooltip={
          header.hasConditionalTooltip &&
          cellsWithTooltip?.[rowIndex]?.[header.rowKey]
        }
        message={cellValue || ''}
      >
        <Typography
          component="div"
          ref={ref =>
            getCellRef(
              ref,
              rowIndex,
              header.rowKey,
              header.hasConditionalTooltip
            )
          }
          className={classNames(
            'row-cell-value',
            classes.cellValue,
            {
              [classes.highlightedText]: isHighlighted,
            },
            header.cellValueClass
          )}
        >
          {cellValue}
        </Typography>
      </ConditionalTooltip>
    );
  };

  const renderRow = (rowData, header, rowIndex) => {
    return (
      <div
        id={header.rowKey}
        key={`row_cell_${rowData.id}_column_${header.id}`}
        className={classNames(classes.rowCell, 'row-cell', header.rowCellClass)}
        style={getAlignItem(header)}
        onClick={handleActionCellClick(header.isHeaderAction)}
      >
        {renderCellValue(
          rowData[header.rowKey],
          header,
          rowIndex,
          header.isHighlighted || rowData.isHighlighted
        )}
      </div>
    );
  };

  return (
    <div className={classes.main}>
      {rows.map((row, index) => (
        <Link
          id={row.id}
          key={`row_${row.id}_${index}`}
          className={classes.row}
          style={getColumnWidth(headers)}
          to={prepareDetailsUrl(row.id)}
        >
          {headers.map(h => renderRow(row, h, index, cellsTooltipRef.current))}
        </Link>
      ))}
    </div>
  );
};

GridTablePlainBody.propTypes = {
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      rowKey: PropTypes.string.isRequired,
      align: PropTypes.string,
      isHighlighted: PropTypes.bool,
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      rowCellClass: PropTypes.string,
    })
  ).isRequired,
  prepareDetailsUrl: PropTypes.func.isRequired,
};

export default GridTablePlainBody;
